import { NIL as NIL_UUID } from "uuid";
import NothingHere from "../../../../../../../assets/Illustrations/NothingHere.svg";
import { FluentInput } from "../../../../../../../components/FluentUI/Fluent-Input";
import { FluentDropdown } from "../../../../../../../components/FluentUI/Fluent-Dropdown";
import { FluentDatePicker } from '../../../../../../../components/FluentUI/Fluent-DatePicker.jsx';
import { getFormattedDate } from '../../../../../../../util/time-helpers.js';

export const whenModalEventTypes = [
  "Task Created",
  "Task Status Changed To",
  "Form Status Changed To",
  "Form Submission",
  "Field Input"
];

export const fieldInputTypes = [
  "Save Form",
  "Value"
  // "Submit Form", // Will be added in future
  // "Live Input" // Will be added in future
];

export const thenModalEventTypes = [
  "Field Availability",
  "Field Reset",
  "Field Insert",
  "Form Availability",
  "Section Availability",
  "Change Form Status To",
  "Reset to Status",
];

export const DEFAULT_WHEN_EVENT_STATE = {
  Id: NIL_UUID,
  DynamicFormAutomationId: NIL_UUID,
  RelatedTo: null,
  RelatedToCondition: null,
  EventType: null,
  EventTypeValue: null,
  EventFormId: null,
  EventFormName: "",
  EventDynamicFieldId: null,
  EventDynamicFieldName: "",
  EventTypeTrigger: null,
  EventTypeStatusId: null,
  EventTypeStatusName: ""
};

export const DEFAULT_THEN_EVENT_STATE = {
  Id: NIL_UUID,
  DynamicFormAutomationId: NIL_UUID,
  RelatedTo: null,
  RelatedToCondition: null,
  EventType: null,
  EventTypeValue: null,
  EventDynamicFieldId: null,
  EventDynamicFieldName: "",
  EventDynamicFormFieldId: null,
  EventTypeStatus: null,
  EventTypeStatusName: "",
  EventDynamicFieldVisible: null,
  EventDynamicFieldEditable: null,
  EventDynamicFieldMandatory: null,
  EventDynamicSectionId: null,
  EventDynamicSectionName: "",
  EventFormId: null,
  EventFormName: "",
  EventTypeTrigger: null,
  EventTypeStatusId: null
};

export const DEFAULT_DYNAMIC_FORM_AUTOMATION_THEN = {
  DynamicFormAutomationThenId: NIL_UUID,
  GroupId: null,
  GroupName: "",
  Id: NIL_UUID,
  MemberPermissionId: null,
  MemberPermissionName: null,
  RoleId: null,
  UserId: null,
  UserName: null
};

export const DEFAULT_DYNAMIC_FORM_AUTOMATION_THEN_AVAILABILITY = {
  Id: NIL_UUID,
  DynamicFormAutomationThenId: NIL_UUID,
  UserId: null,
  UserName: null,
  GroupId: null,
  GroupName: "",
  RoleId: null,
  MemberPermissionId: null,
  MemberPermissionName: null
};

export const ValueType = ({ selectedField, eventData, setEventData }) => {
    const handleValueChange = (value) => {
        setEventData((prev) => ({
            ...prev,
            EventTypeValue: value,
        }));
    };
    switch (selectedField?.FieldType) {
        case "Short Text Entry":
        case "Time":
        case "Long Text Entry":
            return (<FluentInput
                label="Field Value"
                placeholder="Enter field value"
                defaultValue={eventData?.EventTypeValue}
                onBlur={(e) => handleValueChange(e.target.value)}
            />);
        case "Numeric (Decimal)":
        case "Numeric (Integer)":
            return (<FluentInput
                label="Field Value"
                placeholder="Enter field value"
                type="number"
                defaultValue={eventData?.EventTypeValue}
                onChange={(e) => handleValueChange(e.target.value)}
            />);
        case "Radio Buttons":
        case "Dropdown":
        case "Checkbox":
            return (<FluentDropdown
                options={selectedField?.LookUpValues.split('|').map((val) => val.trim())}
                label="Field Value"
                placeholder="Enter field value"
                value={eventData?.EventTypeValue}
                isControlled={true}
                labelStyle="font-weight-normal"
                onChange={(e, data) => handleValueChange(data.optionText)}
            />);
        case "Toggle":
            return (<FluentDropdown
                options={["Yes", "No"]}
                label="Field Value"
                placeholder="Enter field value"
                value={eventData?.EventTypeValue}
                isControlled={true}
                labelStyle="font-weight-normal"
                onChange={(e, data) => handleValueChange(data.optionText == "Yes" ? "selected" : "not selected")}
            />);
        case "Date":
            return (<FluentDatePicker
                label="Field Value"
                placeholder="Select a Date"
                size="medium"
                onChange={(date) => handleValueChange(getFormattedDate(date))}
            />);
    }
};

export const isWhenEventValid = (eventData, isChained) => {
    if (!eventData?.EventType) return false;
    if (isChained && !eventData.RelatedToCondition) return false;

    switch (eventData.EventType) {
        case "Task Created":
            return true;
        case "Task Status Changed To":
            return !!eventData.EventTypeStatusId;
        case "Form Status Changed To":
            return !!eventData.EventFormId && !!eventData.EventTypeStatusId;
        case "Form Submission":
            return !!eventData.EventFormId;
        case "Field Input":
            return !!eventData.EventFormId && !!eventData.EventDynamicFieldId;
        default:
            return false;
    }
};

export const isThenEventValid = (eventData, availabilityData, isChained) => {
    if (!eventData?.EventType) return false;
    if (isChained && !eventData.RelatedToCondition) return false;

    const hasAvailability =
        Array.isArray(availabilityData) &&
        availabilityData.length > 0 &&
        availabilityData.some(a => a.UserId || a.GroupId);

    switch (eventData.EventType) {
        case "Field Availability":
            return !!eventData.EventDynamicFieldId && hasAvailability;
        case "Field Reset":
            return !!eventData.EventDynamicFieldId;
        case "Field Insert":
            return !!eventData.EventDynamicFieldId;
        case "Form Availability":
            return eventData.EventTypeValue != null && eventData.EventTypeValue !== "" && hasAvailability;
        case "Section Availability":
            return !!eventData.EventDynamicSectionId && hasAvailability;
        case "Change Form Status To":
        case "Reset to Status":
            return !!eventData.EventTypeStatus || !!eventData.EventTypeStatusName;
        default:
            return false;
    }
};

const SELECTABLE_FIELD_TYPES = ["Checkbox", "Dropdown", "Radio Buttons", "Toggle"];

const hasSelectableOptions = (field) =>
    SELECTABLE_FIELD_TYPES.includes(field?.FieldType) ||
        (!!field?.LookUpValues &&
           field?.LookUpValues !== "|" &&
           field?.LookUpValues !== "https://" &&
           field?.LookUpValues !== "http://" &&
           field?.LookUpValues !== "" &&
           field?.LookUpValues !== null);

export const isWhenEventDuplicate = (eventData, existingWhenEvents, editingIndex = null, fieldsInputs = []) => {
    if (!eventData?.EventType || !Array.isArray(existingWhenEvents)) return false;

    return existingWhenEvents.some((existing, idx) => {
        if (editingIndex !== null && idx === editingIndex) return false;
        if (existing.EventType !== eventData.EventType) return false;

        switch (eventData.EventType) {
            case "Task Created":
                return true;
            case "Task Status Changed To":
                return existing.EventTypeStatusId === eventData.EventTypeStatusId;
            case "Form Status Changed To":
                return existing.EventFormId === eventData.EventFormId &&
                       existing.EventTypeStatusId === eventData.EventTypeStatusId;
            case "Form Submission":
                return existing.EventFormId === eventData.EventFormId;
            case "Field Input": {
                if (existing.EventFormId !== eventData.EventFormId ||
                    existing.EventDynamicFieldId !== eventData.EventDynamicFieldId ||
                    existing.EventTypeTrigger !== eventData.EventTypeTrigger) return false;
                const field = fieldsInputs?.find(f =>
                    eventData.EventDynamicFormFieldId
                        ? f.Id === eventData.EventDynamicFormFieldId
                        : f.DynamicFieldId === eventData.EventDynamicFieldId
                );
                if (hasSelectableOptions(field)) {
                    return existing.EventTypeValue === eventData.EventTypeValue;
                }
                return true;
            }
            default:
                return false;
        }
    });
};

export const isThenEventDuplicate = (eventData, existingThenEvents, editingIndex = null, fieldsInputs = []) => {
    if (!eventData?.EventType || !Array.isArray(existingThenEvents)) return false;

    return existingThenEvents.some((existing, idx) => {
        if (editingIndex !== null && idx === editingIndex) return false;
        const ev = existing.DynamicFormAutomationThen || {};
        if (ev.EventType !== eventData.EventType) return false;

        switch (eventData.EventType) {
            case "Field Availability":
            case "Field Reset":
                return ev.EventDynamicFormFieldId && eventData.EventDynamicFormFieldId
                    ? ev.EventDynamicFormFieldId === eventData.EventDynamicFormFieldId
                    : ev.EventDynamicFieldId === eventData.EventDynamicFieldId;
            case "Field Insert": {
                const isDifferentField = ev.EventDynamicFormFieldId && eventData.EventDynamicFormFieldId
                    ? ev.EventDynamicFormFieldId !== eventData.EventDynamicFormFieldId
                    : ev.EventDynamicFieldId !== eventData.EventDynamicFieldId;
                if (isDifferentField) return false;
                const field = fieldsInputs?.find(f =>
                    eventData.EventDynamicFormFieldId
                        ? f.Id === eventData.EventDynamicFormFieldId
                        : f.DynamicFieldId === eventData.EventDynamicFieldId
                );
                if (hasSelectableOptions(field)) {
                    return ev.EventTypeValue === eventData.EventTypeValue;
                }
                return true;
            }
            case "Form Availability":
                return ev.EventTypeValue === eventData.EventTypeValue;
            case "Section Availability":
                return ev.EventDynamicSectionId === eventData.EventDynamicSectionId;
            case "Change Form Status To":
            case "Reset to Status":
                return true;
            default:
                return false;
        }
    });
};

export const EmptyState = () => {
  return (
    <div className="empty-state-container">
      <img src={NothingHere} alt="No Data" />
      <p className="empty-state-header">
        No Automations yet
      </p>
      <p className="empty-state-subtext">
        Create rules that trigger actions based on user interactions inside this form.
      </p>
    </div>
  );
};